import React, { forwardRef } from 'react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ZAxis } from 'recharts';
import { format, parseISO, subDays, addDays, getTime, differenceInDays } from 'date-fns';

// Define the data structure
export interface ScatterPlotData {
  date: string; // Full date string (ISO format)
  costValue: number; // Cost value on Y-axis
  compName: string;
}

export interface CCReportScatterPlotProps {
  data: ScatterPlotData[];
}

// Utility to format large numbers (Y-Axis)
const formatYAxisValue = (value: number) => {
  if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)}B`;
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
  return value.toFixed(0);
};

// Generate evenly spaced timestamps for X-axis with a buffer and exactly 5 divisions
const generateEvenlySpacedDates = (data: ScatterPlotData[]) => {
  if (data.length === 0) {
    return { xAxisTicks: [], minDate: new Date(), maxDate: new Date() };
  }

  const dates = data
    .map(d => {
      try {
        const parsedDate = parseISO(d.date);
        if (isNaN(parsedDate.getTime())) throw new Error('Invalid date');
        return parsedDate;
      } catch (error) {
        console.error(`Failed to parse date: ${d.date}`, error);
        return null;
      }
    })
    .filter((d): d is Date => d !== null) // TypeScript type guard to remove nulls
    .sort((a, b) => a.getTime() - b.getTime()); // Sort in ascending order

  if (dates.length === 0) {
    return { xAxisTicks: [], minDate: new Date(), maxDate: new Date() };
  }

  let minDate = dates[0];
  let maxDate = dates[dates.length - 1];

  // Find the largest gap between consecutive dates
  let maxGap = 0;
  for (let i = 1; i < dates.length; i++) {
    const gap = differenceInDays(dates[i], dates[i - 1]);
    if (gap > maxGap) {
      maxGap = gap;
    }
  }

  // Dynamic buffer: 30% of the largest gap, with a minimum of 30 days
  const bufferDays = Math.max(Math.ceil(maxGap * 0.3), 30);

  // Apply buffer dynamically
  minDate = subDays(minDate, bufferDays);
  maxDate = addDays(maxDate, bufferDays);

  // Ensure exactly 5 divisions
  const totalDays = differenceInDays(maxDate, minDate);
  const intervalDays = Math.ceil(totalDays / 4); // 5 ticks → 4 gaps

  const tickDates: number[] = [];
  let currentDate = minDate;

  while (currentDate <= maxDate) {
    tickDates.push(currentDate.getTime()); // Store as timestamp
    currentDate = addDays(currentDate, intervalDays); // Use calculated interval
  }

  return { xAxisTicks: tickDates, minDate, maxDate };
};

// Convert date strings to timestamps for plotting
const processScatterData = (data: ScatterPlotData[]) => {
  return data.map(d => ({
    ...d,
    timestamp: getTime(parseISO(d.date)), // Convert to epoch time
  }));
};

// Custom Tooltip for Scatter Plot
const CustomTooltip: React.FC<any> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const { date, costValue, compName } = payload[0].payload;
    const formattedDate = format(parseISO(date), 'dd MMM yyyy'); // Full date format

    return (
      <div
        style={{
          backgroundColor: '#fff',
          border: '1px solid #ccc',
          padding: '10px',
          fontSize: '12px',
          borderRadius: '4px',
          boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.2)',
          minWidth: '120px',
        }}
      >
        <p style={{ margin: 0, fontWeight: 'bold', color: '#333' }}>{compName}</p>
        <hr style={{ margin: '5px 0', border: '0.5px solid #ddd' }} />
        <p style={{ margin: '5px 0', fontWeight: 'bold', color: '#666' }}>{formattedDate}</p>
        <p style={{ margin: 0, fontSize: '11px', color: '#444' }}>
          Cost: <span style={{ fontWeight: 'bold', color: '#000' }}>{`$${formatYAxisValue(costValue)}`}</span>
        </p>
      </div>
    );
  }
  return null;
};

// Custom Tick Component for X-axis to format month-year
const CustomXAxisTick: React.FC<any> = ({ x, y, payload }) => {
  const formattedDate = format(new Date(payload.value), 'MMM yy'); // Example: Jan 24, Jun 24

  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={12} textAnchor='middle' fill='#666' style={{ fontSize: '10px' }}>
        {formattedDate}
      </text>
    </g>
  );
};

const CCReportScatterPlot = forwardRef<HTMLDivElement, CCReportScatterPlotProps>(({ data }, ref) => {
  const processedData = processScatterData(data);
  const { xAxisTicks, minDate, maxDate } = generateEvenlySpacedDates(data);

  // Calculate min and max cost values for buffer
  const minCostValue = Math.floor(Math.min(...processedData.map(d => d.costValue)) * 0.9); // Extend 10% lower
  const maxCostValue = Math.ceil(Math.max(...processedData.map(d => d.costValue)) * 1.1); // Extend 10% higher

  const yAxisTicks = Array.from(
    { length: 6 }, // Force 6 evenly spaced ticks
    (_, i) => minCostValue + (i * (maxCostValue - minCostValue)) / 5,
  );

  return (
    <div ref={ref} style={{ width: '100%', height: '400px' }}>
      <ResponsiveContainer width='100%' height='100%'>
        <ScatterChart margin={{ top: 20, right: 50, left: 20, bottom: 50 }}>
          <CartesianGrid strokeDasharray='3 3' />
          <XAxis
            dataKey='timestamp'
            type='number'
            scale='time'
            domain={[minDate.getTime(), maxDate.getTime()]} // Ensuring the range is correct
            ticks={xAxisTicks} // Explicitly setting our generated tick values
            tick={<CustomXAxisTick />}
            allowDataOverflow
            interval={0} // Force all tick labels to be shown
            label={{ value: 'Commencement Date', position: 'bottom', dy: 25, dx: -15, style: { fontSize: '12px' } }}
          />

          <YAxis
            dataKey='costValue'
            tickFormatter={formatYAxisValue}
            tick={{ fontSize: 10 }}
            domain={[minCostValue, maxCostValue]} // Apply buffer
            tickCount={6} // Force evenly spaced ticks
            ticks={yAxisTicks} // Manually set evenly spaced tick marks
            label={{
              value: 'Cost ($)',
              angle: -90,
              position: 'left',
              dy: -10,
              style: { fontSize: '12px' },
            }}
          />
          <ZAxis range={[70, 70]} />
          <Tooltip content={<CustomTooltip />} />
          <Scatter name='Cost Data' data={processedData} fill='#4E79A7' radius={10} />
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
});
CCReportScatterPlot.displayName = 'CCReportScatterPlot';

export default CCReportScatterPlot;
